import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  Rating,
  Skeleton,
  Alert,
  Breadcrumbs,
  TextField,
  MenuItem,
  Collapse,
  Divider,
  Paper,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StarIcon from '@mui/icons-material/Star';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WifiIcon from '@mui/icons-material/Wifi';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import PoolIcon from '@mui/icons-material/Pool';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import TvIcon from '@mui/icons-material/Tv';
import g2TravelApi from '../../services/g2TravelApi';
import { normalizeHotelRouteId, buildBookingPath } from '../../lib/hotelRoutes';
import Header from '../../components/Header/Header';
import { useCurrency } from '../../context/CurrencyContext';
import './HotelDetail.css';

const getDefaultTomorrowISO = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().split('T')[0];
};

/** Initialise booking bar from URL query (?checkIn=&checkOut=&nights=&adults=) or react-router state. */
const parseBookingFromQueryAndState = (location) => {
  const params = new URLSearchParams(location.search || '');
  const fromState = location.state?.searchParams || {};

  const checkInRaw =
    params.get('checkIn') ||
    params.get('checkInDate') ||
    (fromState.checkIn != null && fromState.checkIn !== '' ? String(fromState.checkIn) : '') ||
    (fromState.checkInDate != null && fromState.checkInDate !== '' ? String(fromState.checkInDate) : '') ||
    '';

  const checkOutRaw =
    params.get('checkOut') ||
    params.get('checkOutDate') ||
    (fromState.checkOut != null && fromState.checkOut !== '' ? String(fromState.checkOut) : '') ||
    (fromState.checkOutDate != null && fromState.checkOutDate !== '' ? String(fromState.checkOutDate) : '') ||
    '';

  let nights = parseInt(params.get('nights') || String(fromState.nights ?? ''), 10);
  if (Number.isNaN(nights) || nights < 1) nights = 1;

  if (checkInRaw && checkOutRaw) {
    const d1 = new Date(`${checkInRaw}T12:00:00`);
    const d2 = new Date(`${checkOutRaw}T12:00:00`);
    if (!Number.isNaN(d1.getTime()) && !Number.isNaN(d2.getTime())) {
      const diff = Math.round((d2 - d1) / 86400000);
      if (diff > 0) nights = diff;
    }
  }

  let adults = parseInt(params.get('adults') ?? String(fromState.adults ?? ''), 10);
  if (Number.isNaN(adults) || adults < 1) {
    const guests = parseInt(params.get('guests') ?? String(fromState.guests ?? ''), 10);
    adults = !Number.isNaN(guests) && guests >= 1 ? guests : 1;
  }
  let children = parseInt(params.get('children') ?? String(fromState.children ?? '0'), 10);
  if (Number.isNaN(children) || children < 0) children = 0;

  return {
    checkInDate: checkInRaw || getDefaultTomorrowISO(),
    nights,
    adults,
    children,
    currency: fromState.currency || params.get('currency') || 'GBP',
    nationality: fromState.nationality || params.get('nationality') || 'GB',
  };
};

/** Row passed from search results — show immediately while full property + rooms load. */
const getListingPreviewHotel = (routeState, routeId) => {
  if (!routeState || !routeId) return null;
  const h = routeState.hotel;
  if (!h || typeof h !== 'object') return null;
  const hid = h.id ?? h.propertyId ?? h.code;
  if (hid == null || String(hid).trim() !== String(routeId).trim()) return null;
  return { ...h, id: h.id ?? h.propertyId ?? routeId };
};

const HotelDetail = () => {
  const { formatFrom, selectedCurrency } = useCurrency();
  const { id: rawRouteId } = useParams();
  const id = useMemo(() => normalizeHotelRouteId(rawRouteId), [rawRouteId]);
  const navigate = useNavigate();
  const location = useLocation();

  const previewHotel = useMemo(
    () => getListingPreviewHotel(location.state, id),
    [id, location.key, location.state]
  );

  const [fetchedHotel, setFetchedHotel] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [priceResults, setPriceResults] = useState(null);
  const [detailsFetching, setDetailsFetching] = useState(true);
  const [priceLoading, setPriceLoading] = useState(true);
  const [error, setError] = useState(null);

  const hotel = fetchedHotel || previewHotel;
  const [expandedSections, setExpandedSections] = useState({
    about: true,
    facilities: false
  });

  const getCheckOutDate = (checkIn, nights) => {
    const checkOut = new Date(`${checkIn}T12:00:00`);
    checkOut.setDate(checkOut.getDate() + nights);
    return checkOut.toISOString().split('T')[0];
  };

  const [bookingDates, setBookingDates] = useState(() => parseBookingFromQueryAndState(location));

  useEffect(() => {
    setBookingDates(parseBookingFromQueryAndState(location));
  }, [id, location.search, location.key]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setFetchedHotel(null);
    setRooms([]);
    setError(null);
    setDetailsFetching(true);

    let cancelled = false;
    (async () => {
      try {
        const response = await g2TravelApi.getHotelDetails(id);
        if (cancelled) return;
        if (response.success && response.hotel) {
          setFetchedHotel(response.hotel);
          setRooms(response.rooms || []);
        } else {
          const msg =
            (typeof response.error === 'string' && response.error) ||
            response.error?.description ||
            response.error?.code ||
            'Hotel not found';
          setError(msg);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setDetailsFetching(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!id) return;
    fetchPrices();
    window.scrollTo(0, 0);
  }, [id, bookingDates.checkInDate, bookingDates.nights, bookingDates.adults, bookingDates.children, selectedCurrency]);

  const fetchPrices = async () => {
    try {
      setPriceLoading(true);
      const searchParams = {
        distributionId: "PL1662",
        nationality: "GB",
        checkInDate: bookingDates.checkInDate,
        nights: bookingDates.nights,
        currency: selectedCurrency || "GBP",
        propertyId: id,
        roomPax: [{
          id: 1,
          adults: bookingDates.adults,
          children: bookingDates.children
        }],
        includeRoomNames: true,
        includeRoomCapacity: true,
        includeResultsHavingNoCancelPolicy: true,
        includeMSP: true,
        includeSupplements: true,
        timeoutMillis: 120000
      };

      const result = await g2TravelApi.searchPrices(searchParams);
      setPriceResults(result);
    } catch (err) {
      console.error('Error fetching prices:', err);
    } finally {
      setPriceLoading(false);
    }
  };

  const getStarRating = (ratingString) => {
    if (!ratingString) return 0;
    const match = ratingString.match(/(\d+)/);
    return match ? parseInt(match[1]) : 0;
  };

  const handleBookRoom = (option) => {
    navigate(buildBookingPath(id), {
      state: {
        hotel,
        searchParams: bookingDates,
        selectedOption: option,
        priceResults
      }
    });
  };

  const handleGoHome = () => {
    navigate('/');
    window.scrollTo(0, 0);
  };

  /** Return to hotel listing with the same query string as this detail URL (dates, location, etc.). */
  const handleBackToSearchResults = () => {
    const search = location.search || '';
    navigate({ pathname: '/hotels', search });
    window.scrollTo(0, 0);
  };

  const handleNavigate = (page) => {
    const routes = {
      'home': '/',
      'listing': '/hotels',
      'about': '/about',
      'contact': '/contact',
      'test-g2': '/test-g2'
    };
    navigate(routes[page] || '/');
    window.scrollTo(0, 0);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (!hotel && detailsFetching) {
    return (
      <>
        <Header onLogoClick={handleGoHome} onNavigate={handleNavigate} />
        <Box className="hotel-detail-page hotel-detail-page--loading">
          <Box className="hotel-detail-container hotel-detail-back-row">
            <Button
              type="button"
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToSearchResults}
              className="hotel-detail-back-to-results"
            >
              Back to search results
            </Button>
          </Box>
          <Box className="hotel-detail-container hotel-detail-loading-panel">
            <CircularProgress size={48} sx={{ color: '#003580' }} aria-hidden />
            <Typography component="h1" className="hotel-detail-loading-title">
              Loading hotel details…
            </Typography>
            <Typography variant="body2" color="text.secondary" className="hotel-detail-loading-sub">
              Fetching property information and rooms. This may take a few seconds.
            </Typography>
            <Skeleton variant="rectangular" height={220} className="hotel-detail-loading-skel" />
            <Skeleton variant="text" height={32} className="hotel-detail-loading-skel-text" />
            <Skeleton variant="text" width="55%" />
          </Box>
        </Box>
      </>
    );
  }

  if (!hotel && !detailsFetching) {
    return (
      <>
        <Header onLogoClick={handleGoHome} onNavigate={handleNavigate} />
        <Box className="hotel-detail-page">
          <Box className="hotel-detail-container hotel-detail-back-row">
            <Button
              type="button"
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToSearchResults}
              className="hotel-detail-back-to-results"
            >
              Back to search results
            </Button>
          </Box>
          <Box className="hotel-detail-container">
            <Alert severity="error">{error || 'Hotel not found'}</Alert>
          </Box>
        </Box>
      </>
    );
  }

  const starRating = getStarRating(hotel.rating);
  const propertyOptions = priceResults?.propertyOption?.[0]?.option || [];
  const checkOutDate = getCheckOutDate(bookingDates.checkInDate, bookingDates.nights);

  // Get meal plan badge color
  const getMealBadgeColor = (mealPlanCode) => {
    if (mealPlanCode === 'RO') return { bg: '#E3F2FD', color: '#1976D2', label: 'Room Only' };
    if (mealPlanCode === 'RB') return { bg: '#FFF3E0', color: '#F57C00', label: 'Room & Breakfast' };
    if (mealPlanCode === 'HB') return { bg: '#F3E5F5', color: '#7B1FA2', label: 'Half Board' };
    if (mealPlanCode === 'FB') return { bg: '#E8F5E9', color: '#388E3C', label: 'Full Board' };
    return { bg: '#F5F5F5', color: '#616161', label: mealPlanCode };
  };

  return (
    <>
      <Header onLogoClick={handleGoHome} onNavigate={handleNavigate} />
      {detailsFetching && hotel ? (
        <LinearProgress
          className="hotel-detail-fetch-progress"
          aria-label="Loading latest property details"
        />
      ) : null}
      {error && hotel ? (
        <Box className="hotel-detail-container hotel-detail-inline-alert-wrap">
          <Alert severity="warning" className="hotel-detail-inline-alert">
            {error}
          </Alert>
        </Box>
      ) : null}
      <Box className="hotel-detail-page">
        {/* Breadcrumbs */}
        <Box className="hotel-detail-breadcrumbs">
          <Box className="hotel-detail-container hotel-detail-breadcrumbs-inner">
            <Button
              type="button"
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToSearchResults}
              className="hotel-detail-back-to-results"
            >
              Back to search results
            </Button>
            <Breadcrumbs aria-label="breadcrumb" className="hotel-detail-breadcrumbs-trail">
              <Link to="/" className="hotel-detail-breadcrumb-link">Home</Link>
              <Link to="/hotels" className="hotel-detail-breadcrumb-link">Hotels</Link>
              <Link to="/hotels" className="hotel-detail-breadcrumb-link">{hotel.countryCode || 'Location'}</Link>
              <Typography className="hotel-detail-breadcrumb-current">{hotel.name}</Typography>
            </Breadcrumbs>
          </Box>
        </Box>

        {/* Top Header Section */}
        <Box className="hotel-detail-header-section">
          <Box className="hotel-detail-container">
            <Box className="hotel-detail-header">
              <Box className="hotel-detail-header-left">
                <Typography className="hotel-detail-name">{hotel.name}</Typography>
                <Box className="hotel-detail-location">
                  <LocationOnIcon className="hotel-detail-location-icon" />
                  <Typography className="hotel-detail-address">
                    {hotel.address1 || ''}
                    {hotel.address2 && `, ${hotel.address2}`}
                    {hotel.zipCode && `, ${hotel.zipCode}`}
                    {hotel.cityName && `, ${hotel.cityName}`}
                    {hotel.countryName && `, ${hotel.countryName}`}
                  </Typography>
                </Box>
                {starRating > 0 && (
                  <Box className="hotel-detail-rating">
                    <Rating value={starRating} readOnly className="hotel-detail-stars" />
                    <Typography className="hotel-detail-rating-text">{starRating}-Star Hotel</Typography>
                  </Box>
                )}
              </Box>
              <Box className="hotel-detail-header-right">
                {propertyOptions.length > 0 && (
                  <Box className="hotel-detail-price-badge">
                    <Typography className="hotel-detail-price-label">Starting from</Typography>
                    <Typography className="hotel-detail-price-value">
                      {formatFrom(Math.floor(Math.min(...propertyOptions.map(o => o.totalPrice || 0))), 'GBP')}
                    </Typography>
                    <Typography className="hotel-detail-price-period">per night</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Main Content Area */}
        <Box className="hotel-detail-container">
          <Box className="hotel-detail-content">
            {/* Main Content (70%) */}
            <Box className="hotel-detail-main">
              {/* About This Property */}
              <Paper className="hotel-detail-section">
                <Typography className="hotel-detail-section-title">About this property</Typography>
                <Collapse in={expandedSections.about} collapsedSize={100}>
                  <Typography className="hotel-detail-description">
                    {hotel.description || 'This property offers comfortable accommodations in a convenient location. Experience quality service and modern amenities during your stay.'}
                  </Typography>
                </Collapse>
                {hotel.description && hotel.description.length > 100 && (
                  <Button
                    className="hotel-detail-show-more"
                    onClick={() => toggleSection('about')}
                    endIcon={expandedSections.about ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  >
                    {expandedSections.about ? 'Show less' : 'Show more'}
                  </Button>
                )}
              </Paper>

              {/* Most Popular Facilities */}
              <Paper className="hotel-detail-section">
                <Typography className="hotel-detail-section-title">Most popular facilities</Typography>
                <Box className="hotel-detail-facilities-grid">
                  {[
                    { icon: <WifiIcon />, label: 'Free WiFi' },
                    { icon: <LocalParkingIcon />, label: 'Parking' },
                    { icon: <RestaurantIcon />, label: 'Restaurant' },
                    { icon: <FitnessCenterIcon />, label: 'Fitness center' },
                    { icon: <PoolIcon />, label: 'Swimming pool' },
                    { icon: <AcUnitIcon />, label: 'Air conditioning' },
                    { icon: <TvIcon />, label: 'TV' },
                    { icon: <CheckCircleIcon />, label: 'Non-smoking rooms' }
                  ].map((facility, index) => (
                    <Box key={index} className="hotel-detail-facility-item">
                      {facility.icon}
                      <Typography className="hotel-detail-facility-label">{facility.label}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>

              {/* Availability Section */}
              <Paper className="hotel-detail-section hotel-detail-availability">
                <Typography className="hotel-detail-section-title">Availability</Typography>
                <Box className="hotel-detail-availability-form">
                  <TextField
                    type="date"
                    label="Check-in"
                    value={bookingDates.checkInDate}
                    onChange={(e) => setBookingDates({ ...bookingDates, checkInDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    className="hotel-detail-date-input"
                  />
                  <TextField
                    type="date"
                    label="Check-out"
                    value={checkOutDate}
                    onChange={(e) => {
                      const newCheckOut = new Date(e.target.value);
                      const newCheckIn = new Date(bookingDates.checkInDate);
                      const nights = Math.ceil((newCheckOut - newCheckIn) / (1000 * 60 * 60 * 24));
                      if (nights > 0) {
                        setBookingDates({ ...bookingDates, nights });
                      }
                    }}
                    InputLabelProps={{ shrink: true }}
                    className="hotel-detail-date-input"
                  />
                  <TextField
                    select
                    label="Adults"
                    value={bookingDates.adults}
                    onChange={(e) => setBookingDates({ ...bookingDates, adults: parseInt(e.target.value) })}
                    className="hotel-detail-guest-input"
                  >
                    {[1, 2, 3, 4, 5, 6].map(num => (
                      <MenuItem key={num} value={num}>{num}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Children"
                    value={bookingDates.children}
                    onChange={(e) => setBookingDates({ ...bookingDates, children: parseInt(e.target.value) })}
                    className="hotel-detail-guest-input"
                  >
                    {[0, 1, 2, 3, 4].map(num => (
                      <MenuItem key={num} value={num}>{num}</MenuItem>
                    ))}
                  </TextField>
                  <Button 
                    variant="contained" 
                    className="hotel-detail-change-search-btn"
                    onClick={fetchPrices}
                  >
                    Change search
                  </Button>
                </Box>
              </Paper>

              {/* Room Options Section */}
              <Paper className="hotel-detail-section">
                <Typography className="hotel-detail-section-title">
                  Available rooms & prices
                </Typography>
                {priceLoading ? (
                  <Box className="hotel-detail-loading">
                    <Skeleton variant="rectangular" height={150} sx={{ mb: 2 }} />
                    <Skeleton variant="rectangular" height={150} />
                  </Box>
                ) : propertyOptions.length === 0 ? (
                  <Alert severity="info">No rooms available for the selected dates. Please try different dates.</Alert>
                ) : (
                  <Box className="hotel-detail-rooms-table">
                    {propertyOptions.map((option, index) => {
                      const roomPrice = option.roomPrice?.[0];
                      const roomName = roomPrice?.roomName || `Room Option ${index + 1}`;
                      const ratePlanName = roomPrice?.ratePlan?.name || '';
                      const roomCode = roomPrice?.roomCode || '';
                      const mealPlanCode = roomPrice?.mealPlanCode;
                      const mealBadge = mealPlanCode ? getMealBadgeColor(mealPlanCode) : null;
                      const cancellation = roomPrice?.cancellationCharge?.[0];
                      const matchedRoom = rooms.find(r => r.roomTypeCode === roomCode || r.code === roomCode);
                      
                      return (
                        <Box key={index} className="hotel-detail-room-row">
                          <Box className="hotel-detail-room-info">
                            <Typography className="hotel-detail-room-name">
                              {roomName}
                              {ratePlanName && (
                                <>
                                  <span className="hotel-detail-room-separator"> — </span>
                                  <span className="hotel-detail-room-rate-plan">{ratePlanName}</span>
                                </>
                              )}
                            </Typography>
                            {roomCode && (
                              <Typography className="hotel-detail-room-code">Room Code: {roomCode}</Typography>
                            )}
                            <Box className="hotel-detail-room-badges">
                              {mealBadge && (
                                <Chip 
                                  label={mealBadge.label} 
                                  size="small"
                                  className="hotel-detail-meal-badge"
                                  sx={{ 
                                    backgroundColor: mealBadge.bg, 
                                    color: mealBadge.color,
                                    fontWeight: 600
                                  }}
                                />
                              )}
                            </Box>
                            {matchedRoom && (
                              <Box className="hotel-detail-room-details">
                                {matchedRoom.beds && (
                                  <Typography className="hotel-detail-room-detail">
                                    🛏️ {matchedRoom.beds} Bed{matchedRoom.beds > 1 ? 's' : ''}
                                  </Typography>
                                )}
                                {matchedRoom.standardOccupancy && (
                                  <Typography className="hotel-detail-room-detail">
                                    👥 Standard: {matchedRoom.standardOccupancy} guest{matchedRoom.standardOccupancy > 1 ? 's' : ''}
                                  </Typography>
                                )}
                                {matchedRoom.maximumOccupancy && (
                                  <Typography className="hotel-detail-room-detail">
                                    ⭐ Max: {matchedRoom.maximumOccupancy} guest{matchedRoom.maximumOccupancy > 1 ? 's' : ''}
                                  </Typography>
                                )}
                              </Box>
                            )}
                            {cancellation && (
                              <Typography className="hotel-detail-cancellation">
                                Cancellation: Free before {new Date(cancellation.dateTime).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                          <Box className="hotel-detail-room-price">
                            <Typography className="hotel-detail-room-price-value">
                              {formatFrom(Math.floor(option.totalPrice || 0), 'GBP')}
                            </Typography>
                            <Typography className="hotel-detail-room-price-period">per night</Typography>
                            <Chip 
                              label="✓ Lowest price" 
                              size="small" 
                              className="hotel-detail-lowest-badge"
                              sx={{ backgroundColor: '#4CAF50', color: 'white', fontWeight: 600 }}
                            />
                            <Button
                              variant="contained"
                              className="hotel-detail-book-btn"
                              onClick={() => handleBookRoom(option)}
                            >
                              Book Now
                            </Button>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Paper>

              {/* Room Types Information */}
              {rooms.length > 0 && (
                <Paper className="hotel-detail-section">
                  <Typography className="hotel-detail-section-title">
                    Room Types ({rooms.length})
                  </Typography>
                  <Box className="hotel-detail-room-types-grid">
                    {rooms.map((room, index) => (
                      <Box key={index} className="hotel-detail-room-type-card">
                        <Typography className="hotel-detail-room-type-name">
                          {room.roomTypeName || room.name || `Room Type ${index + 1}`}
                        </Typography>
                        {room.beds && (
                          <Typography className="hotel-detail-room-type-detail">
                            🛏️ {room.beds} Bed{room.beds > 1 ? 's' : ''}
                          </Typography>
                        )}
                        {room.standardOccupancy && (
                          <Typography className="hotel-detail-room-type-detail">
                            👥 {room.standardOccupancy} guest{room.standardOccupancy > 1 ? 's' : ''}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Paper>
              )}

              {/* Facilities Section */}
              <Paper className="hotel-detail-section">
                <Typography className="hotel-detail-section-title">
                  Facilities of {hotel.name}
                </Typography>
                <Box className="hotel-detail-facilities-list">
                  <Box className="hotel-detail-facility-category">
                    <Typography className="hotel-detail-facility-category-title">Most popular facilities</Typography>
                    <Box className="hotel-detail-facility-items">
                      {['Free WiFi', 'Parking', 'Restaurant', 'Fitness center', 'Swimming pool', 'Air conditioning'].map((item, idx) => (
                        <Box key={idx} className="hotel-detail-facility-item-list">
                          <CheckCircleIcon className="hotel-detail-facility-check" />
                          <Typography>{item}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Box>

            {/* Sidebar (30%) */}
            <Box className="hotel-detail-sidebar">
              {/* Review Score Card */}
              <Paper className="hotel-detail-sidebar-card">
                <Box className="hotel-detail-review-score">
                  {starRating > 0 ? (
                    <>
                      <Box className="hotel-detail-review-badge">
                        <Typography className="hotel-detail-review-score-value">{starRating}.0</Typography>
                        <Typography className="hotel-detail-review-score-label">Excellent</Typography>
                      </Box>
                      <Typography className="hotel-detail-review-count">
                        Based on {Math.floor(Math.random() * 500 + 100)} reviews
                      </Typography>
                    </>
                  ) : (
                    <Typography>No reviews yet</Typography>
                  )}
                </Box>
              </Paper>

              {/* Property Highlights */}
              <Paper className="hotel-detail-sidebar-card">
                <Typography className="hotel-detail-sidebar-title">Property highlights</Typography>
                <Box className="hotel-detail-highlights">
                  {[
                    'Free WiFi',
                    'Parking available',
                    'Restaurant on site',
                    'Fitness center',
                    starRating >= 4 ? 'Highly rated' : null
                  ].filter(Boolean).map((highlight, idx) => (
                    <Box key={idx} className="hotel-detail-highlight-item">
                      <CheckCircleIcon className="hotel-detail-highlight-icon" />
                      <Typography>{highlight}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default HotelDetail;
